import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import crssrImage from "../../../../images/singleinvoice/cross-red.svg";
import typeOfContract from "../../../../constants/typeOfContract";
import { setNotification } from "../../../../features/notification/notificationSlice.js";
import { setProjectAdded } from "../../../../features/projects/projectSlice.js";
import {
  useUpdateBillableCostRate,
  useUpdateProjectCost,
} from "../../../../components/TimeTracker/TimeTrackerTeam/TimeTrackerTeamMembers/teamMemberApi.jsx";
import "./MvpBillableRateModal.css";

function memberName(item) {
  if (item?.first_name) {
    return [item.first_name, item.middle_name && item.middle_name !== "null" ? item.middle_name : "", item.last_name]
      .filter(Boolean)
      .join(" ");
  }
  if (item?.company_first_name) {
    return `${item.company_first_name} ${item.company_last_name || ""}`.trim();
  }
  return item?.company_name ? `${item.company_name} (Company)` : "";
}

function currencySymbol(code) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: code || "USD" })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value || "$";
  } catch (_error) {
    return "$";
  }
}

function convert(amount, from, to, forex) {
  const fromRate = forex?.[from];
  const toRate = forex?.[to];
  const value = Number(amount) || 0;
  if (!fromRate || !toRate) return value;
  return (value / fromRate) * toRate;
}

function hourlyDisplay(item, clientCurrency, forex) {
  const raw = Number(item?.cost) || 0;
  const hours = parseInt(item?.monthly_hours, 10) || 0;
  const hourly = item?.typeOfContract === typeOfContract.Hourly_Rate || !hours ? raw : raw / hours;
  const shown = convert(hourly, item?.payment_currency || clientCurrency, clientCurrency, forex);
  return String(Math.round(shown * 100) / 100);
}

function storedCost(entered, item, clientCurrency, forex) {
  const inPayment = convert(entered, clientCurrency, item?.payment_currency || clientCurrency, forex);
  if (item?.typeOfContract === typeOfContract.Hourly_Rate) return inPayment;
  const hours = parseInt(item?.monthly_hours, 10) || 0;
  return hours ? inPayment * hours : inPayment;
}

const MvpBillableRateModal = ({
  isOpen,
  onClose,
  item,
  projectId,
  clientCurrency,
  billableAmount,
  canEditCost,
  onCostUpdated,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { forex } = useSelector((state) => state.forex);
  const token = user?.token;
  const updateBillable = useUpdateProjectCost(token);
  const updateCost = useUpdateBillableCostRate(token);
  const [billable, setBillable] = useState("");
  const [costRate, setCostRate] = useState("");
  const [saving, setSaving] = useState(false);
  const symbol = currencySymbol(clientCurrency);

  useEffect(() => {
    if (!isOpen) return;
    setBillable(billableAmount === 0 || billableAmount ? String(billableAmount) : "");
    setCostRate(hourlyDisplay(item, clientCurrency, forex));
  }, [isOpen, billableAmount, item, clientCurrency, forex]);

  const save = async (event) => {
    event.preventDefault();
    if (billable === "" || Number.isNaN(Number(billable))) {
      toast.error("Billable cost cannot be empty!", { position: "bottom-right", autoClose: 2000 });
      return;
    }
    if (canEditCost && (costRate === "" || Number.isNaN(Number(costRate)))) {
      toast.error("Cost rate cannot be empty!", { position: "bottom-right", autoClose: 2000 });
      return;
    }
    setSaving(true);
    try {
      const billableResponse = await updateBillable.mutateAsync({
        projectId,
        userId: item._id,
        amount: Number(billable),
      });
      if (billableResponse?.data?.status === "error") throw new Error("billable");
      let nextCost = item?.cost;
      if (canEditCost) {
        nextCost = storedCost(costRate, item, clientCurrency, forex);
        const costResponse = await updateCost.mutateAsync({
          selectedId: item._id,
          cost: nextCost,
        });
        if (costResponse?.data?.status === "error") throw new Error("cost");
        onCostUpdated?.(item._id, nextCost);
      }
      toast.success("Billable rate updated successfully", { position: "bottom-right", autoClose: 2000 });
      dispatch(setNotification(Date.now()));
      dispatch(setProjectAdded());
      onClose();
    } catch (_error) {
      toast.error("Something went wrong", { position: "bottom-right", autoClose: 2000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="mvp-rate-modal mobile-action-modal"
      overlayClassName="mvp-rate-overlay mobile-sheet-overlay"
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Edit Billable Rate"
      closeTimeoutMS={280}
      style={{
        overlay: {
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "stretch",
          backgroundColor: "rgba(40, 40, 40, 0.45)",
          zIndex: 10000,
        },
        content: {
          position: "fixed",
          inset: "auto 0 0 0",
          top: "auto",
          left: 0,
          right: 0,
          bottom: 0,
          transform: "none",
          width: "100%",
          maxWidth: "100%",
          height: "auto",
          maxHeight: "85vh",
          margin: 0,
          overflow: "auto",
        },
      }}
    >
      <form onSubmit={save}>
        <div className="mvp-rate-head">
          <button type="button" className="mvp-rate-close" onClick={onClose} aria-label="Close">
            <img src={crssrImage} alt="" />
          </button>
          <h2>Edit Billable Rate</h2>
        </div>
        <label className="mvp-rate-label">
          <span>User</span>
          <input className="mvp-rate-user" value={memberName(item)} readOnly />
        </label>
        <label className="mvp-rate-label">
          <span>Billable Rate</span>
          <div className="mvp-rate-field">
            <span>{symbol}</span>
            <input
              inputMode="decimal"
              value={billable}
              onChange={(event) => setBillable(event.target.value)}
            />
          </div>
        </label>
        {canEditCost && (
          <label className="mvp-rate-label">
            <span>Cost Rate / Hr</span>
            <div className="mvp-rate-field">
              <span>{symbol}</span>
              <input
                inputMode="decimal"
                value={costRate}
                onChange={(event) => setCostRate(event.target.value)}
              />
            </div>
          </label>
        )}
        <button type="submit" className="mvp-rate-save" disabled={saving}>
          Save Changes
        </button>
      </form>
    </Modal>
  );
};

export default MvpBillableRateModal;
